import type { ApiClient } from '@sentinel/api-client';
import { SKIP_UNAUTH_HEADER, type UserRole } from '@sentinel/api-client';
import type { SessionImpersonator, SessionOrg, SessionTenant, SessionUser } from '../session/hooks';
import { readExpiresAt, rotateAccessToken } from './access-token-refresh';

export interface SessionPayload {
  user: SessionUser;
  /**
   * The organization the session is scoped to, from the `org_id` / `org_slug`
   * userinfo claims. `null` when the caller belongs to no organization, which
   * is a different state from belonging to one with no tenant — the console
   * shows different guidance for each.
   */
  org: SessionOrg | null;
  /** `null` when the session is not scoped to a tenant (org admins may have none). */
  tenant: SessionTenant | null;
  roles: string[];
  /**
   * True when the backend's `OrgAdminUser` gate would admit this caller, i.e.
   * the `is_org_admin` claim on `/auth/userinfo`. Membership-derived and
   * distinct from the global `role` — consumers must use this rather than a
   * role-string check to mirror the org-admin authorization boundary.
   */
  isOrgAdmin: boolean;
  /** Non-null while another principal is impersonating `user`. */
  impersonator: SessionImpersonator | null;
}

interface UserInfoActor {
  /** OIDC `act` claim — the acting admin's subject. */
  sub?: string;
  email?: string | null;
}

interface UserInfoResponse {
  sub: string;
  email?: string | null;
  name?: string | null;
  mfa_enabled?: boolean;
  local_auth_managed_by_idp?: boolean;
  is_org_admin?: boolean;
  is_tenant_admin?: boolean;
  role?: UserRole;
  org_id?: string | null;
  org_slug?: string | null;
  tenant_id?: string | null;
  tenant_slug?: string | null;
  entitled_products?: string[];
  /**
   * Optional impersonation actor. Backend may surface this under either the
   * OIDC-standard `act` claim or a friendlier `impersonator` field; we accept
   * either so the rollout doesn't depend on a specific naming choice.
   */
  act?: UserInfoActor | null;
  impersonator?: UserInfoActor | null;
}

export function extractImpersonator(u: UserInfoResponse): SessionImpersonator | null {
  const actor = u.impersonator ?? u.act;
  if (!actor || !actor.sub) return null;
  return { id: actor.sub, email: actor.email ?? '' };
}

/**
 * Fetches the current session by calling the OIDC userinfo endpoint and
 * mapping its claims into our internal {@link SessionPayload} shape.
 *
 * Returns `null` on 401 (unauthenticated) and throws for other errors so
 * callers can surface them.
 */
export async function fetchSession(client: ApiClient): Promise<SessionPayload | null> {
  // Cold-load fast path: if we have positive evidence the access token is
  // already expired (a *past* `expires_at` estimate written by a prior
  // rotation), skip the doomed first `/auth/userinfo` probe — it would only
  // 401 — and rotate straight away. The estimate tracks the real token TTL
  // (the `/auth/token` refresh response returns `expires_in` even in cookie
  // mode), so a past value reliably means "expired". A `null` estimate
  // (unknown — e.g. localStorage cleared, or a session minted in another
  // context) or a future value keep the probe-first path, so this is a strict
  // improvement with no behavior change in those cases. In the rare cold edge
  // where the rotate succeeds but the follow-up userinfo still 401s, the
  // post-probe 401 handler below rotates once more as a bounded,
  // lock-serialised safety net.
  const expiresAt = readExpiresAt();
  if (expiresAt !== null && expiresAt <= Date.now()) {
    const rotated = await rotateAccessToken(client);
    if (rotated.status !== 'ok' && rotated.status !== 'lost-race') return null;
  }

  // Skip-header: a 401 here means "not logged in" (initial probe), not
  // "session lost mid-use" — suppress the unauth dispatcher.
  let { data, error, response } = await client.GET('/auth/userinfo', {
    headers: { [SKIP_UNAUTH_HEADER]: '1' },
  });
  if (response?.status === 401) {
    // Cold-load with expired access token but valid refresh: rotate once and
    // retry, otherwise route guards bounce the user to /login despite having
    // a usable refresh-token cookie. `rotateAccessToken` serialises via
    // `navigator.locks`, so a concurrent scheduler tick will not double-fire.
    const rotated = await rotateAccessToken(client);
    if (rotated.status !== 'ok' && rotated.status !== 'lost-race') return null;
    ({ data, error, response } = await client.GET('/auth/userinfo', {
      headers: { [SKIP_UNAUTH_HEADER]: '1' },
    }));
    if (response?.status === 401) return null;
  }
  if (error) throw error;
  const u = data as UserInfoResponse | undefined;
  if (!u) return null;

  const user: SessionUser = {
    id: u.sub,
    email: u.email ?? '',
    mfaEnabled: Boolean(u.mfa_enabled),
    localAuthManagedByIdp: Boolean(u.local_auth_managed_by_idp),
    ...(u.name ? { name: u.name } : {}),
  };
  const org: SessionOrg | null = u.org_id ? { id: u.org_id, slug: u.org_slug ?? '' } : null;
  const tenant: SessionTenant | null = u.tenant_id
    ? { id: u.tenant_id, slug: u.tenant_slug ?? '' }
    : null;
  const roles = u.role ? [u.role] : [];
  return {
    user,
    org,
    tenant,
    roles,
    isOrgAdmin: Boolean(u.is_org_admin),
    impersonator: extractImpersonator(u),
  };
}
